import React, { useState } from "react";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import { LuPlay } from "react-icons/lu";
import { CATEGORY_NAMES, STATUS } from "../../../services/agent-eval";
import { StatusPill, Transcript } from "./components";
import ScenarioDetails from "./scenario-details";

/**
 * Every scenario, grouped by area, with its latest result.
 *
 * Results are the point of this page, so a scenario row leads with its status
 * and — when it did not pass — the reason in plain words. The conversation and
 * the expectation are one click away for anyone who wants to see why.
 */

const RANK = { [STATUS.FAILED]: 0, [STATUS.ERROR]: 1, notRun: 2, [STATUS.PASSED]: 3 };

export const FILTERS = [
	["all", "All"],
	["failed", "Needs attention"],
	["passed", "Passed"],
	["notRun", "Not run yet"],
];

function statusOf(result) {
	return result?.status && RANK[result.status] !== undefined ? result.status : "notRun";
}

// Results from before plain-language reasons fall back to their first failed check.
function reasonOf(result) {
	if (!result) return "";
	const failed = (result.checks || []).find((check) => !check.passed);
	return result.reason || result.error || (failed && (failed.detail || failed.label)) || "";
}

function matches(filter, status) {
	if (filter === "failed") return status === STATUS.FAILED || status === STATUS.ERROR;
	if (filter === "passed") return status === STATUS.PASSED;
	if (filter === "notRun") return status === "notRun";
	return true;
}

function ScenarioRow({ scenario, result, onRun, running }) {
	const status = statusOf(result);
	const [open, setOpen] = useState(false);
	const attention = status === STATUS.FAILED || status === STATUS.ERROR;
	const reason = reasonOf(result);

	return (
		<div className="border border-gray-200 rounded-lg bg-white">
			<div className="flex items-start gap-2 px-3 py-2.5">
				<button
					type="button"
					onClick={() => setOpen(!open)}
					aria-expanded={open}
					className="flex flex-1 min-w-0 items-start gap-2 text-left"
				>
					{open ? (
						<HiChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					) : (
						<HiChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					)}
					<span className="min-w-0">
						<span className="block text-sm font-medium text-gray-800">{scenario.name}</span>
						{scenario.targetName && scenario.category !== "knowledge_base" && (
							<span className="block text-xs text-gray-400">{scenario.targetName}</span>
						)}
						{attention && reason && !open && (
							<span className={`block text-xs mt-0.5 ${status === STATUS.FAILED ? "text-red-600" : "text-amber-600"}`}>
								{reason}
							</span>
						)}
					</span>
				</button>
				<StatusPill status={status} />
				<button
					type="button"
					onClick={() => onRun([scenario.id])}
					disabled={running}
					title="Run this scenario"
					aria-label={`Run ${scenario.name}`}
					className="p-1 text-gray-400 hover:text-primary disabled:opacity-40"
				>
					<LuPlay className="h-4 w-4" />
				</button>
			</div>

			{open && (
				<div className="border-t border-gray-100 px-4 py-3 grid gap-4 lg:grid-cols-2">
					<ScenarioDetails scenario={scenario} />
					<div className="space-y-3 text-sm">
						<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What happened</p>
						{!result ? (
							<p className="text-gray-500">This scenario has not been run yet.</p>
						) : (
							<>
								{reason && (
									<p className={`rounded-lg px-3 py-2 ${
										status === STATUS.PASSED ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300"
											: status === STATUS.FAILED ? "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300"
												: "bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"}`}>
										{reason}
									</p>
								)}
								<Transcript turns={result.turns || []} toolCalls={result.toolCalls || []} />
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

function Area({ categoryId, scenarios, results, onRun, running }) {
	const [collapsed, setCollapsed] = useState(false);
	const passed = scenarios.filter((s) => statusOf(results[s.id]) === STATUS.PASSED).length;
	const ran = scenarios.filter((s) => statusOf(results[s.id]) !== "notRun").length;
	const ordered = [...scenarios].sort((a, b) => RANK[statusOf(results[a.id])] - RANK[statusOf(results[b.id])]);

	return (
		<section className="space-y-2">
			<div className="flex items-center gap-2">
				<button type="button" onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-left">
					{collapsed ? <HiChevronRight className="h-4 w-4 text-gray-400" /> : <HiChevronDown className="h-4 w-4 text-gray-400" />}
					<h3 className="text-sm font-semibold text-gray-800">{CATEGORY_NAMES[categoryId] || categoryId}</h3>
				</button>
				<span className="text-xs text-gray-400">
					{ran ? `${passed} of ${ran} passed` : `${scenarios.length} scenario${scenarios.length === 1 ? "" : "s"}`}
				</span>
				<button
					type="button"
					onClick={() => onRun(scenarios.map((s) => s.id))}
					disabled={running}
					className="ml-auto text-xs text-primary hover:underline disabled:opacity-40"
				>
					Run {CATEGORY_NAMES[categoryId] || "these"}
				</button>
			</div>
			{!collapsed && (
				<div className="space-y-1.5">
					{ordered.map((scenario) => (
						<ScenarioRow key={scenario.id} scenario={scenario} result={results[scenario.id]} onRun={onRun} running={running} />
					))}
				</div>
			)}
		</section>
	);
}

export default function ScenarioList({ plan, results, filter, onRun, running }) {
	const areas = (plan?.categories || [])
		.map((category) => [
			category.id,
			(category.cases || [])
				.map((c) => ({ ...c, category: category.id }))
				.filter((c) => matches(filter, statusOf(results[c.id]))),
		])
		.filter(([, scenarios]) => scenarios.length);

	if (!areas.length) {
		return <p className="text-sm text-gray-400 text-center py-8">Nothing to show for this filter.</p>;
	}

	return (
		<div className="space-y-6">
			{areas.map(([categoryId, scenarios]) => (
				<Area key={categoryId} categoryId={categoryId} scenarios={scenarios} results={results} onRun={onRun} running={running} />
			))}
		</div>
	);
}
